import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import userService from '../../services/userService';
import { useBeta } from '../../contexts/BetaContext';
import './UserInfoModal.css';

const UserInfoModal = ({ onSubmit, onCancel, isLoading, closePathOverride = null, forceReturningUser = false }) => {
  const navigate = useNavigate();
  const { basePath } = useBeta();
  const [isReturningUser, setIsReturningUser] = useState(forceReturningUser);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [checkingUser, setCheckingUser] = useState(false);
  const [existingUser, setExistingUser] = useState(null);
  const [hasCheckedUser, setHasCheckedUser] = useState(false);
  const emailCheckTimerRef = useRef(null);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (forceReturningUser) {
      setIsReturningUser(true);
    }
  }, [forceReturningUser]);

  // Check for existing user when email changes (debounced) - only for returning users
  useEffect(() => {
    if (!isReturningUser) {
      setExistingUser(null);
      setHasCheckedUser(false);
      return;
    }

    if (!email || !validateEmail(email)) {
      setExistingUser(null);
      setHasCheckedUser(false);
      return;
    }

    // Clear existing timer
    if (emailCheckTimerRef.current) {
      clearTimeout(emailCheckTimerRef.current);
    }

    setCheckingUser(true);
    setHasCheckedUser(false);

    // Debounce email check (500ms after user stops typing)
    emailCheckTimerRef.current = setTimeout(async () => {
      try {
        const user = await userService.getUserByEmail(email);
        if (user) {
          setExistingUser(user);
          // Pre-fill name if user exists
          if (user.name && !firstName && !lastName) {
            const nameParts = user.name.split(' ');
            setFirstName(nameParts[0] || '');
            setLastName(nameParts.slice(1).join(' ') || '');
          }
        } else {
          setExistingUser(null);
        }
      } catch (err) {
        console.error('Error checking for existing user:', err);
        setExistingUser(null);
      } finally {
        setCheckingUser(false);
        setHasCheckedUser(true);
      }
    }, 500);

    return () => {
      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current);
      }
      setCheckingUser(false);
    };
  }, [email, firstName, lastName, isReturningUser]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate email (always required)
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate name (only for new users)
    if (!isReturningUser) {
      if (!firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
    }

    // For returning users, must have found an existing user
    if (isReturningUser && !existingUser) {
      newErrors.email = 'No account found with this email. Please check your email or create a new account.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const fullName = isReturningUser && existingUser 
      ? existingUser.name 
      : `${firstName.trim()} ${lastName.trim()}`;
    onSubmit({
      name: fullName,
      email: email.toLowerCase().trim(),
      existingUser: existingUser // Pass existing user info if found
    });
  };

  const handleSwitchMode = () => {
    setIsReturningUser(!isReturningUser);
    setEmail('');
    setFirstName('');
    setLastName('');
    setExistingUser(null);
    setErrors({});
  };

  const handleClose = () => {
    navigate(closePathOverride || basePath || '/');
  };

  return (
    <div className="user-info-modal-overlay">
      <div className="user-info-modal">
        <button
          type="button"
          className="modal-close-btn"
          onClick={handleClose}
          aria-label="Close"
        >
          ×
        </button>
        <div className="user-info-modal-header">
          <h2>Welcome!</h2>
          <p>
            {forceReturningUser
              ? 'Enter your email to find and load your ballot:'
              : isReturningUser
              ? 'Enter your email to find and load your existing ballot:'
              : 'To save your progress and submit your ballot, we need a few details:'}
          </p>
        </div>

        {!forceReturningUser && (
          <div className="user-mode-toggle">
            {isReturningUser ? (
              <button
                type="button"
                onClick={handleSwitchMode}
                className="mode-toggle-link"
                disabled={isLoading}
              >
                New user? Start here
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSwitchMode}
                className="mode-toggle-link"
                disabled={isLoading}
              >
                Already have a ballot?
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="user-info-form">
          {!isReturningUser && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="user-first-name">
                  First Name <span className="required">*</span>
                </label>
                <input
                  id="user-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (errors.firstName) setErrors({ ...errors, firstName: null });
                  }}
                  placeholder="First Name"
                  className={errors.firstName ? 'user-info-modal__input--error' : ''}
                  disabled={isLoading}
                />
                {errors.firstName && <span className="user-info-modal__error-text">{errors.firstName}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="user-last-name">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  id="user-last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (errors.lastName) setErrors({ ...errors, lastName: null });
                  }}
                  placeholder="Last Name"
                  className={errors.lastName ? 'user-info-modal__input--error' : ''}
                  disabled={isLoading}
                />
                {errors.lastName && <span className="user-info-modal__error-text">{errors.lastName}</span>}
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="user-email">
              Email <span className="required">*</span>
            </label>
            <input
              id="user-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: null });
              }}
              placeholder="Enter your email"
              className={errors.email ? 'user-info-modal__input--error' : ''}
              disabled={isLoading}
            />
            {errors.email && <span className="user-info-modal__error-text">{errors.email}</span>}
            {isReturningUser && checkingUser && (
              <small className="help-text checking">
                Looking up your account...
              </small>
            )}
            {isReturningUser && existingUser && !checkingUser && (
              <div className="existing-user-message">
                <span className="check-icon">✓</span>
                <span>
                  Welcome back, {existingUser.displayName || existingUser.name || 'there'}!
                  We found your account and will load your ballot.
                </span>
              </div>
            )}
            {isReturningUser && !existingUser && !checkingUser && hasCheckedUser && email && validateEmail(email) && (
              <div className="no-user-found-message">
                <span className="info-icon">ℹ</span>
                <span>No account found with this email. Please check your email or create a new account.</span>
              </div>
            )}
            {!isReturningUser && !checkingUser && email && validateEmail(email) && (
              <small className="help-text">
                We'll use this to save your ballot.
              </small>
            )}
          </div>

          <div className="user-info-modal__form-actions">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="user-info-modal__btn user-info-modal__btn--secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
            )}
            <div className="user-info-modal__btn-wrap">
              <button
                type="submit"
                className="user-info-modal__btn user-info-modal__btn--primary"
                disabled={isLoading || (isReturningUser && !existingUser)}
              >
                {isLoading
                  ? 'Loading...'
                  : isReturningUser
                    ? 'Load My Ballot'
                    : 'Continue'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserInfoModal;
